import { z } from 'zod'

import { GetCompanyPaymentTermsSchema, PaymentTermsResponseSchema } from './payment-terms'
import { GetContactSchema } from './contact'
import { GetCustomerRiskProfileSchema } from './risk-profile'

export const WeightUnitSchema = z.enum(['kg', 't'])

export type WeightUnit = z.infer<typeof WeightUnitSchema>

export const GetLoadCustomAttributesSchema = z.object({
  export: z.string().nullish(),
  import: z.string().nullish(),
  ipaff: z.string().nullish(),
})

const GetLoadEndCompanyBaseSchema = z.object({
  id: z.number(),
  organisation_id: z.number().nullish(),
  short_title: z.string().nullish(),
  title: z.string(),
})

export const GetLoadEndCustomerCompanySchema = GetLoadEndCompanyBaseSchema.extend({
  payment_terms: GetCompanyPaymentTermsSchema.nullish(),
  risk_profile: GetCustomerRiskProfileSchema.nullable(),
})

export const GetLoadEndCarrierCompanySchema = GetLoadEndCompanyBaseSchema.extend({
  carrier_payment_terms: GetCompanyPaymentTermsSchema.nullish(),
})

const GetLoadEndCompanyRelationSchema = z.object({
  broker: z
    .object({
      id: z.number(),
      title: z.string(),
    })
    .nullable(),
  contact: z
    .object({
      email: z.string().nullable(),
      id: z.number(),
      last_name: z.string().nullish(),
      name: z.string().nullable(),
      phone: z.string().nullable(),
    })
    .nullable(),
  created_at: z.coerce.date(),
  created_by: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullable(),
  id: z.number(),
  updated_at: z.coerce.date(),
})

export const GetLoadEndCustomerSchema = GetLoadEndCompanyRelationSchema.extend({
  company: GetLoadEndCustomerCompanySchema.nullable(),
  relation: z.literal('end_customer'),
})

export const GetLoadEndCarrierSchema = GetLoadEndCompanyRelationSchema.extend({
  company: GetLoadEndCarrierCompanySchema.nullable(),
  relation: z.literal('end_carrier'),
})

export const GetLoadEndCompanySchema = z.discriminatedUnion('relation', [
  GetLoadEndCustomerSchema,
  GetLoadEndCarrierSchema,
])

export const GetLoadSchema = z.object({
  carrier_payment_terms: PaymentTermsResponseSchema.nullish().transform(value => value ?? null),
  contact_id: z.number().nullish(),
  created_at: z.coerce.date(),
  currency: z.string().nullish(),
  customer_invoice_reference_number: z.string().nullish(),
  customer_order_number: z.string().nullish(),
  customs: z.boolean(),
  destination_distance: z.number().nullish(),
  distance: z.number(),
  end_carrier: GetLoadEndCarrierSchema.nullish(),
  end_customer: GetLoadEndCustomerSchema.nullish(),
  estimated_profit: z.number().nullable().default(null),
  goods_description: z.string().nullish(),
  haulier_contact: GetContactSchema.nullish(),
  haulier_contact_id: z.number().nullish(),
  haulier_notes: z.string().nullish(),
  haulier_order_reference: z.string().nullish(),
  height: z.number(),
  id: z.number(),
  international: z.boolean(),
  length: z.number().nullish(),
  load_date: z.coerce.date().nullish(),
  notes: z.string().nullish(),
  organisation_id: z.number().nullish(),
  origin_distance: z.number().nullish(),
  pallets: z.number().nullish(),
  payment_terms: PaymentTermsResponseSchema.nullish().transform(value => value ?? null),
  pod_requirement: z
    .object({
      alias: z.string().nullish(),
      id: z.number().nullish(),
      title: z.string().nullish(),
    })
    .nullish(),
  pod_requirement_description: z.string().nullish(),
  profit_base: z.number().nullable().default(null),
  ready: z.boolean(),
  registration_plates: z
    .object({
      trailer: z.string().nullish(),
      truck: z.string().nullish(),
    })
    .nullish(),
  source_load_id: z.number().nullish(),
  temperature: z.number().nullish(),
  total_costs_base: z.number().nullable().default(null),
  total_revenue_base: z.number().nullable().default(null),
  transit_time: z.number().nullish(),
  unload_date: z.coerce.date().nullable(),
  updated_at: z.string(),
  urgent: z.boolean(),
  user_id: z.number().nullish(),
  weight: z.number().nullish(),
  weight_unit: WeightUnitSchema.nullish(),
  width: z.number().nullish(),
})

export const LoadMutationResponseSchema = GetLoadSchema.omit({
  estimated_profit: true,
  profit_base: true,
  total_costs_base: true,
  total_revenue_base: true,
})
  .partial()
  .extend({
    estimated_profit: z.number().nullable().optional(),
    id: z.number(),
    profit_base: z.number().nullable().optional(),
    total_costs_base: z.number().nullable().optional(),
    total_revenue_base: z.number().nullable().optional(),
  })

export const GetLoadTypeSchema = z.object({
  id: z.number(),
  slug: z.string(),
  title: z.string(),
})

export const GetPackagingTypeSchema = z.object({
  alias: z.string().optional(),
  id: z.number(),
  title: z.string(),
})

export const LoadStatusAliasSchema = z.enum([
  'draft',
  'to_cover',
  'transport_planning',
  'waiting_for_transit',
  'waiting_for_arrival_confirmation',
  'arrived_at_collection',
  'loading_started',
  'in_transit',
  'arrived_at_destination',
  'unloading_confirmed',
  'waiting_for_docs',
  'completed',
  'archived',
  'cancelled',
])

export const GetLoadStatusSchema = z.object({
  alias: LoadStatusAliasSchema,
  id: z.number(),
  order: z.number(),
  title: z.string(),
})

export const UpdateLoadStatusInputSchema = z.object({
  notes: z.string().optional(),
  reason: z.string().optional(),
  status: LoadStatusAliasSchema,
})

export const GetRequirementsSchema = z.object({
  adr: z.boolean(),
  dvs: z.boolean(),
  waste_license: z.boolean(),
})

/**
 * One Organisation base currency's total for a Loads list column, in minor units.
 * `currency`/`amount` are set only when the server confirmed every matching Load shares
 * one non-base original currency. `amount_base: null` means no Load in the group has a value.
 */
export const LoadsTotalsCurrencyGroupSchema = z.object({
  amount: z.number().nullable(),
  amount_base: z.number().nullable(),
  base_currency: z.string().nullable(),
  currency: z.string().nullable(),
  loads_count: z.number(),
  missing_count: z.number(),
})

/** Totals for every Load matching the Loads list criteria, keyed by table column id. */
export const GetLoadsTotalsSchema = z.object({
  // A null column is one the user may not view.
  columns: z.record(z.string(), LoadsTotalsCurrencyGroupSchema.array().nullable()),
  loads_count: z.number(),
})

export type GetLoadsTotals = z.infer<typeof GetLoadsTotalsSchema>
export type LoadsTotalsCurrencyGroup = z.infer<typeof LoadsTotalsCurrencyGroupSchema>
